import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Divider, Fade
} from '@mui/material';
import { Mail, Lock, User, Phone, IdCard } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const InfoCard = styled(Paper)(({ theme }) => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
});

export default function ContaConfiguracoes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) setUser(res.data.user);
      } catch {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(stored);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleRequestReset = async () => {
    if (!user?.email) return;
    setResetLoading(true);
    setResetMsg({ type: '', text: '' });
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/forgot-password`,
        { email: user.email },
        { timeout: 15000 }
      );
      if (res.data.success) {
        setResetMsg({ type: 'success', text: 'Email de redefinição enviado! Verifique sua caixa de entrada.' });
      } else {
        setResetMsg({ type: 'error', text: res.data.message || 'Erro ao enviar email.' });
      }
    } catch {
      setResetMsg({ type: 'error', text: 'Erro ao conectar com o servidor.' });
    } finally {
      setResetLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Typography variant="h4" sx={{ color: '#fff', mb: 1 }}>
          Minha Conta
        </Typography>
        <Typography sx={{ color: '#9CA3AF', mb: 4 }}>
          Gerencie suas informações pessoais e segurança.
        </Typography>

        {/* Informações pessoais */}
        <InfoCard>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Informações Pessoais
          </Typography>
          <InfoRow>
            <User size={20} strokeWidth={1.75} color="#7C3AED" />
            <Box>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>Nome</Typography>
              <Typography sx={{ color: '#fff' }}>{user?.nome || '-'}</Typography>
            </Box>
          </InfoRow>
          <Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.1)' }} />
          <InfoRow>
            <Mail size={20} strokeWidth={1.75} color="#7C3AED" />
            <Box>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>Email</Typography>
              <Typography sx={{ color: '#fff' }}>{user?.email || '-'}</Typography>
            </Box>
          </InfoRow>
          <Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.1)' }} />
          <InfoRow>
            <IdCard size={20} strokeWidth={1.75} color="#7C3AED" />
            <Box>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>CPF</Typography>
              <Typography sx={{ color: '#fff' }}>{user?.cpf || 'Não informado'}</Typography>
            </Box>
          </InfoRow>
          <Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.1)' }} />
          <InfoRow>
            <Phone size={20} strokeWidth={1.75} color="#7C3AED" />
            <Box>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>Telefone</Typography>
              <Typography sx={{ color: '#fff' }}>{user?.telefone || 'Não informado'}</Typography>
            </Box>
          </InfoRow>
        </InfoCard>

        {/* Segurança */}
        <InfoCard>
          <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
            Segurança
          </Typography>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem', mb: 3 }}>
            Solicite um email com link para redefinir sua senha.
          </Typography>

          {resetMsg.text && (
            <Alert severity={resetMsg.type} sx={{ mb: 2 }} onClose={() => setResetMsg({ type: '', text: '' })}>
              {resetMsg.text}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={resetLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Lock size={20} strokeWidth={1.75} />}
            onClick={handleRequestReset}
            disabled={resetLoading}
            sx={{
              background: '#7C3AED',
              borderRadius: '12px', textTransform: 'none', fontWeight: 600, px: 3,
              '&:hover': { background: '#6D28D9' }
            }}
          >
            {resetLoading ? 'Enviando...' : 'Enviar Email de Redefinição de Senha'}
          </Button>
        </InfoCard>
      </Box>
    </Fade>
  );
}
